/*
 * DO NOT DELETE — base-info and page-design tags are consumed by project-snapshot tooling for quick page overview. Always update them to reflect actual page content.
 * <base-info>
 * Description: Detailed list of tokens within a specific category for editing and live preview
 * Style referenceFiles:
 * Design for: Desktop
 * </base-info>
 * <page-design>
 * ## Features & Interactions
 * - View list/table of tokens in the category
 * - Edit token values (with color picker for colors)
 * - Real-time preview of token changes on sample components
 * - Navigate to: TokenOverview (Back)
 *
 * ## Basic Layout
 * Column split: Sidebar + Main (Split: Token List + Live Preview Panel)
 *
 * ## Reuse Components
 * - `LayoutSidebar`
 *
 * ## Page Layout
 * `flex` Column split: Sidebar + Content `flex-1`
 *
 * **Sidebar**: Reuse `LayoutSidebar`, activeKey="tokens"
 *
 * **Main Content** `flex flex-col h-screen`:
 * - **Header** `flex items-center`: Back button + Category Name (e.g., "Colors") + [Add Token] button
 * - **Workspace** `flex-1 flex overflow-hidden`:
 *   - **Token List** `flex-1 flex flex-col overflow-auto`:
 *     - Table layout: Name, Value (editable input + swatch), Layer (Primitive/Semantic), Used In (count).
 *   - **Live Preview Panel** `w-96 flex flex-col border-l`:
 *     - **Header**: "Live Preview"
 *     - **Preview Content** `flex-1 overflow-auto`: Rendered sample components (Buttons, Cards, Alerts) that automatically react to token value changes in the adjacent table.
 *
 * ## Mock Data
 * Category: Colors.
 * Tokens: `blue-500` (#3B82F6), `color-primary` (refs `blue-500`), `button-bg` (refs `color-primary`).
 * Preview Panel: Contains a Primary Button, a Secondary Button, and an Info Card to show color usage.
 *
 * ## Reference
 * - canvas/[Feature] DesignToken/user-flow.md
 * </page-design>
 */

import { useState, useMemo } from 'react'
import { useAtomValue, useSetAtom } from 'jotai'
import { Link } from 'react-router-dom'
import { Icon } from '@iconify/react'
import { LayoutSidebar } from '@/components/function/LayoutSidebar'
import * as TokenCategoryDetailStore from '@/stores/TokenCategoryDetailStore'
import { pageLinks } from '@/pageLinks'

const layerLabels: Record<string, string> = {
  primitive: '基础层',
  semantic: '语义层',
  component: '组件层',
}
const layerStyles: Record<string, string> = {
  primitive: 'bg-purple-50 text-purple-600',
  semantic: 'bg-[#1D263A]/5 text-[#1D263A]',
  component: 'bg-emerald-50 text-emerald-600',
}
const categoryLabels: Record<string, string> = {
  color: '颜色',
  typography: '排版',
  spacing: '间距',
  shadow: '阴影',
  border: '边框',
  radius: '圆角',
}

function AddTokenModal({ category, onClose }: { category: string; onClose: () => void }) {
  const addToken = useSetAtom(TokenCategoryDetailStore.addTokenAtom)
  const [name, setName] = useState('')
  const [value, setValue] = useState('')
  const [layer, setLayer] = useState('primitive')
  const [desc, setDesc] = useState('')
  const [submitting, setSubmitting] = useState(false)

  const handleSubmit = async () => {
    setSubmitting(true)
    try {
      await addToken({ name, value, type: category, layer, description: desc || undefined })
      onClose()
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-[24px] shadow-[0_8px_30px_rgb(0,0,0,0.1)] w-[480px] p-6" onClick={e => e.stopPropagation()}>
        <h2 className="text-lg font-bold mb-6">添加 Token</h2>
        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium text-gray-600 mb-1.5 block">名称 *</label>
            <input type="text" value={name} onChange={e => setName(e.target.value)} placeholder="例如：blue-500" className="w-full px-4 py-3 bg-gray-50 rounded-xl text-sm outline-none focus:ring-1 focus:ring-[#1D263A]/15" />
          </div>
          <div>
            <label className="text-sm font-medium text-gray-600 mb-1.5 block">值 *</label>
            <div className="flex gap-2">
              {category === 'color' && <input type="color" value={value || '#000000'} onChange={e => setValue(e.target.value)} className="w-12 h-12 rounded-xl border-0 cursor-pointer" />}
              <input type="text" value={value} onChange={e => setValue(e.target.value)} placeholder={category === 'color' ? '#3B82F6' : '输入值...'} className="flex-1 px-4 py-3 bg-gray-50 rounded-xl text-sm outline-none focus:ring-1 focus:ring-[#1D263A]/15 font-mono" />
            </div>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-600 mb-1.5 block">层级</label>
            <select value={layer} onChange={e => setLayer(e.target.value)} className="w-full px-4 py-3 bg-gray-50 rounded-xl text-sm outline-none focus:ring-1 focus:ring-[#1D263A]/15">
              <option value="primitive">基础层</option>
              <option value="semantic">语义层</option>
              <option value="component">组件层</option>
            </select>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-600 mb-1.5 block">描述</label>
            <input type="text" value={desc} onChange={e => setDesc(e.target.value)} placeholder="Token 的用途说明..." className="w-full px-4 py-3 bg-gray-50 rounded-xl text-sm outline-none focus:ring-1 focus:ring-[#1D263A]/15" />
          </div>
        </div>
        <div className="flex justify-end gap-3 mt-6">
          <button onClick={onClose} className="px-5 py-2.5 rounded-xl text-sm font-medium text-gray-600 bg-gray-50 hover:bg-gray-100 transition-all">取消</button>
          <button onClick={handleSubmit} disabled={submitting || !name.trim() || !value.trim()} className="bg-[#1D263A] hover:bg-[#151D2E] disabled:opacity-50 text-white font-medium py-2.5 px-5 rounded-xl shadow-[0_4px_12px_rgba(29,38,58,0.25)] transition-all text-sm">{submitting ? '添加中...' : '添加 Token'}</button>
        </div>
      </div>
    </div>
  )
}

function TokenTable() {
  const { data, isPending } = useAtomValue(TokenCategoryDetailStore.refreshAtom)
  const updateToken = useSetAtom(TokenCategoryDetailStore.updateTokenAtom)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editValue, setEditValue] = useState('')

  const isColor = data?.category === 'color'

  const handleStartEdit = (id: string, currentValue: string) => {
    setEditingId(id)
    setEditValue(currentValue)
  }

  const handleSave = async (id: string) => {
    await updateToken({ id, value: editValue })
    setEditingId(null)
  }

  if (isPending) {
    return (
      <div className="animate-pulse space-y-3 p-4">
        {[...Array(6)].map((_, i) => <div key={i} className="h-14 bg-gray-50 rounded-xl" />)}
      </div>
    )
  }

  if (!data || data.tokens.length === 0) {
    return <div className="text-center py-16 text-gray-400"><Icon icon="lucide:database" className="text-4xl mb-3 mx-auto" /><p>暂无 Token</p></div>
  }

  return (
    <div className="divide-y divide-gray-50">
      <div className="grid grid-cols-[1fr_1fr_120px_80px] gap-4 px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
        <span>名称</span>
        <span>值</span>
        <span>层级</span>
        <span>引用</span>
      </div>
      {data.tokens.map(token => (
        <div key={token.id} className="grid grid-cols-[1fr_1fr_120px_80px] gap-4 px-5 py-3.5 items-center hover:bg-gray-50/50 transition-colors">
          <div>
            <span className="text-sm font-mono font-medium">{token.name}</span>
            {token.description && <p className="text-xs text-gray-400 mt-0.5">{token.description}</p>}
          </div>
          <div className="flex items-center gap-2">
            {isColor && <div className="w-7 h-7 rounded-lg shadow-inner border border-gray-100 shrink-0" style={{ backgroundColor: editingId === token.id ? editValue : token.value }} />}
            {editingId === token.id ? (
              <div className="flex items-center gap-1.5 flex-1">
                {isColor && <input type="color" value={editValue || '#000000'} onChange={e => setEditValue(e.target.value)} className="w-7 h-7 rounded border-0 cursor-pointer shrink-0" />}
                <input type="text" value={editValue} onChange={e => setEditValue(e.target.value)} className="flex-1 px-2.5 py-1.5 bg-white rounded-lg text-sm font-mono outline-none ring-1 ring-[#1D263A]/15" autoFocus onKeyDown={e => { if (e.key === 'Enter') void handleSave(token.id); if (e.key === 'Escape') setEditingId(null) }} />
                <button onClick={() => handleSave(token.id)} className="text-[#1D263A] hover:text-[#151D2E]"><Icon icon="lucide:check" /></button>
                <button onClick={() => setEditingId(null)} className="text-gray-400 hover:text-gray-600"><Icon icon="lucide:x" /></button>
              </div>
            ) : (
              <button onClick={() => handleStartEdit(token.id, token.value)} className="text-sm font-mono text-gray-700 hover:text-[#1D263A] transition-colors text-left">{token.value}</button>
            )}
          </div>
          <span className={`px-2.5 py-1 text-xs font-medium rounded-full w-fit ${layerStyles[token.layer] || 'bg-gray-100 text-gray-600'}`}>{layerLabels[token.layer] || token.layer}</span>
          <span className="text-xs text-gray-400">{token.referencesTokenId ? '1 引用' : '-'}</span>
        </div>
      ))}
    </div>
  )
}

function LivePreview() {
  const { data } = useAtomValue(TokenCategoryDetailStore.refreshAtom)

  const colorMap = useMemo(() => {
    const map: Record<string, string> = {}
    if (data) {
      for (const t of data.tokens) {
        map[t.name] = t.value
      }
    }
    return map
  }, [data])

  const primaryColor = colorMap['color-primary'] || colorMap['blue-500'] || '#6366F1'
  const secondaryColor = colorMap['color-secondary'] || colorMap['gray-200'] || '#E5E7EB'
  const successColor = colorMap['color-success'] || colorMap['green-500'] || '#22C55E'

  return (
    <div className="w-96 border-l border-gray-100 flex flex-col h-full overflow-hidden bg-white">
      <div className="p-5 border-b border-gray-100">
        <h2 className="font-bold text-sm">实时预览</h2>
        <p className="text-xs text-gray-400 mt-1">Token 值变更后自动更新</p>
      </div>
      <div className="flex-1 overflow-y-auto p-5 space-y-6">
        <section>
          <h3 className="text-xs font-semibold text-gray-500 mb-3">按钮</h3>
          <div className="flex flex-wrap gap-3">
            <button className="px-5 py-2.5 rounded-xl text-sm font-medium text-white shadow-[0_4px_12px_rgba(0,0,0,0.15)] transition-all" style={{ backgroundColor: primaryColor }}>主要按钮</button>
            <button className="px-5 py-2.5 rounded-xl text-sm font-medium transition-all" style={{ backgroundColor: secondaryColor, color: '#374151' }}>次要按钮</button>
            <button className="px-5 py-2.5 rounded-xl text-sm font-medium text-white transition-all" style={{ backgroundColor: successColor }}>成功按钮</button>
          </div>
        </section>

        <section>
          <h3 className="text-xs font-semibold text-gray-500 mb-3">卡片</h3>
          <div className="rounded-2xl p-4 shadow-[0_8px_30px_rgb(0,0,0,0.03)]" style={{ borderLeft: `4px solid ${primaryColor}` }}>
            <h4 className="font-semibold text-sm mb-1">信息卡片</h4>
            <p className="text-xs text-gray-500">这是一个使用主色调作为左侧边框的信息卡片示例。</p>
          </div>
        </section>

        <section>
          <h3 className="text-xs font-semibold text-gray-500 mb-3">徽标</h3>
          <div className="flex flex-wrap gap-2">
            <span className="px-3 py-1 rounded-full text-xs font-medium text-white" style={{ backgroundColor: primaryColor }}>Primary</span>
            <span className="px-3 py-1 rounded-full text-xs font-medium" style={{ backgroundColor: secondaryColor, color: '#374151' }}>Secondary</span>
            <span className="px-3 py-1 rounded-full text-xs font-medium text-white" style={{ backgroundColor: successColor }}>Success</span>
          </div>
        </section>

        <section>
          <h3 className="text-xs font-semibold text-gray-500 mb-3">提示框</h3>
          <div className="rounded-xl p-4" style={{ backgroundColor: `${primaryColor}15` }}>
            <div className="flex items-center gap-2 mb-1">
              <Icon icon="lucide:info" className="text-sm" style={{ color: primaryColor }} />
              <span className="text-sm font-medium" style={{ color: primaryColor }}>提示信息</span>
            </div>
            <p className="text-xs text-gray-600">这是一条使用当前主题色的提示消息。</p>
          </div>
        </section>
      </div>
    </div>
  )
}

export default function TokenCategoryDetail() {
  const { data, isPending } = useAtomValue(TokenCategoryDetailStore.refreshAtom)
  const [showAddToken, setShowAddToken] = useState(false)

  const categoryName = data?.category ? (categoryLabels[data.category] || data.category) : ''

  return (
    <div className="flex h-screen w-full bg-[#F8F9FA] font-['Noto_Sans_SC',sans-serif] text-[#1A1D25] overflow-hidden">
      <LayoutSidebar />
      <div className="flex-1 flex flex-col h-full overflow-hidden">
        <header className="flex items-center justify-between p-6 pb-0 mb-6 px-8">
          <div className="flex items-center gap-3">
            <Link to={pageLinks.TokenOverview()} className="w-10 h-10 rounded-xl bg-white flex items-center justify-center text-gray-600 shadow-[0_2px_10px_rgb(0,0,0,0.02)] hover:shadow-[0_4px_12px_rgb(0,0,0,0.06)] transition-all"><Icon icon="lucide:arrow-left" className="text-xl" /></Link>
            <h1 className="text-2xl font-bold tracking-tight">{categoryName || '加载中...'}</h1>
            {data && <span className="text-sm text-gray-500">{data.tokens.length} 个 Token</span>}
          </div>
          <button onClick={() => setShowAddToken(true)} className="bg-[#1D263A] hover:bg-[#151D2E] text-white font-medium py-2.5 px-5 rounded-xl shadow-[0_4px_12px_rgba(29,38,58,0.25)] transition-all text-sm flex items-center gap-2"><Icon icon="lucide:plus" />添加 Token</button>
        </header>

        <div className="flex-1 flex overflow-hidden">
          <div className="flex-1 overflow-y-auto bg-white mx-6 mb-6 rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.03)]">
            <TokenTable />
          </div>
          {data?.category === 'color' && <LivePreview />}
        </div>
      </div>

      {showAddToken && data && <AddTokenModal category={data.category} onClose={() => setShowAddToken(false)} />}
    </div>
  )
}
